import * as React from "react"
import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"

/** Cream/stone alternation by band index — the same on every band page. */
export const bandBg = (i: number) => (i % 2 === 0 ? "bg-cream" : "bg-stone")

/**
 * The standard copy band on Gisela's pages: a sticky heading in the left
 * column, markdown body (optionally led by a photo) on the right. Pages build
 * an ordered list of these (see @/lib/section-order) so the admin can move
 * sections and the background colours still alternate.
 */
export function TextBand({
  index,
  heading,
  body,
  photo,
  markdown,
  children,
  className,
}: {
  index: number
  heading: string
  body?: string
  photo?: { url: string | null; alt_text: string | null } | null
  markdown?: Components
  /** Extra content under the body — a cross-link, a table, a callout. */
  children?: React.ReactNode
  /** Overrides the body wrapper's classes (e.g. a bordered callout card). */
  className?: string
}) {
  return (
    <section className={`${bandBg(index)} py-16`}>
      <div className="mx-auto grid max-w-6xl grid-cols-1 px-6 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:gap-x-16 lg:px-8">
        <h2 className="lg:sticky lg:top-24 lg:self-start">{heading}</h2>
        <div className="min-w-0">
          {photo?.url && (
            <img src={photo.url} alt={photo.alt_text ?? ""} className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover" />
          )}
          <div className={className ?? "body-content mt-6 text-ink-body"}>
            {body && (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdown}>
                {body}
              </ReactMarkdown>
            )}
            {children}
          </div>
        </div>
      </div>
    </section>
  )
}
